import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap, timeout } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { User } from '../models/user.model';

/** Cookie holding the UM token — same name ACM's frontend uses. */
const TOKEN_COOKIE = 'auth_token';
/** Pre-UM-token storage (the user's email). Cleared on sight. */
const LEGACY_TOKEN_KEY = 'sp_auth_token';

interface CheckAuthResponse {
  code:    number;
  status:  string;
  message: string;
  results: {
    id:        number;
    full_name: string;
    email:     string;
    uuid:      string;
    /** UM-issued token — sent to our backend as `Authorization: Bearer <token>`. */
    token?:    string;
    /** Internal department label, e.g. "Planner". */
    department_name?: string;
    /** Human-facing department label, e.g. "Planner And Golive". */
    display_department_name?: string;
    /** JSON-encoded string, e.g. '[{"name":"edit_planning_suite","is_active":true}]' */
    permissions?: string;
  };
}

/**
 * Maps a free-text label to the product IDs shown in the side rail.
 * Keyword-based so it tolerates naming variants
 * (e.g. "Planner And Golive", go_live / golive / go-live, optimiser).
 */
function matchProducts(label: string): string[] {
  const l = label.toLowerCase();
  const allowed: string[] = [];
  if (l.includes('plan')) allowed.push('planning-suite');
  if (l.includes('golive') || l.includes('go live') || l.includes('go-live')) allowed.push('go-live');
  if (l.includes('optimi')) allowed.push('optimiser');
  return allowed;
}

/**
 * Resolves which products the user is entitled to.
 *
 * The department label (display_department_name / department_name) is the
 * authoritative signal — e.g. "Planner And Golive" grants Planning Suite +
 * Go-Live, but NOT Optimiser. We deliberately do NOT union this with the
 * `permissions` array, which can carry stale or cross-product grants that
 * would wrongly surface products (the Optimiser-in-side-nav bug).
 *
 * The permissions array is used only as a fallback for accounts that don't
 * carry a department label.
 */
function deriveAllowedProducts(results: CheckAuthResponse['results']): string[] {
  const dept = `${results.display_department_name ?? ''} ${results.department_name ?? ''}`.trim();
  if (dept) {
    const fromDept = matchProducts(dept);
    if (fromDept.length) return fromDept;
  }
  return deriveFromPermissions(results.permissions);
}

/** Fallback: derive products from active permission names. */
function deriveFromPermissions(permissionsJson?: string): string[] {
  let active: string[] = [];
  try {
    const parsed = JSON.parse(permissionsJson ?? '[]') as Array<{ name?: string; is_active?: boolean }>;
    active = parsed
      .filter(p => p && p.is_active && typeof p.name === 'string')
      .map(p => (p.name as string).toLowerCase());
  } catch {
    return [];
  }

  const allowed: string[] = [];
  if (active.some(n => n.includes('planning'))) allowed.push('planning-suite');
  if (active.some(n => n.includes('go_live') || n.includes('golive') || n.includes('go-live'))) allowed.push('go-live');
  if (active.some(n => n.includes('optimi'))) allowed.push('optimiser');
  return allowed;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private userSource = new BehaviorSubject<User | null>(null);
  readonly currentUser$ = this.userSource.asObservable();

  private authCheck$: Observable<boolean> | null = null;

  constructor(private http: HttpClient) {}

  get isLoggedIn(): boolean { return !!this.userSource.value; }
  get snapshot(): User | null { return this.userSource.value; }

  /**
   * Stores the UM token from check-auth in the `auth_token` cookie, as ACM's
   * frontend does. The interceptor sends it to our backend as a Bearer token;
   * the backend verifies it with UM's check-auth on every (uncached) request.
   */
  static storeToken(token: string): void {
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${TOKEN_COOKIE}=${encodeURIComponent(token)}; path=/; SameSite=Lax${secure}`;
    try { localStorage.removeItem(LEGACY_TOKEN_KEY); } catch { /* ignore */ }
  }

  static clearToken(): void {
    document.cookie = `${TOKEN_COOKIE}=; path=/; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    try { localStorage.removeItem(LEGACY_TOKEN_KEY); } catch { /* ignore */ }
  }

  static getToken(): string {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${TOKEN_COOKIE}=([^;]*)`));
    if (!match) return '';
    try { return decodeURIComponent(match[1]); } catch { return match[1]; }
  }

  checkAuth(): Observable<boolean> {
    if (this.isLoggedIn) return of(true);

    // `bypassAuth` fabricates an Administrator without contacting UM. It is
    // gated on `!environment.production` as well as the flag so that it cannot
    // survive into a production artifact even if a flag is set by mistake —
    // one typo in an environment file should not disable authentication.
    if ((environment as any).bypassAuth && !environment.production) {
      const devUser: User = { id: 0, name: 'Dev User', email: 'dev@localhost' };
      AuthService.storeToken(devUser.email);
      this.userSource.next(devUser);
      return of(true);
    }

    if (!this.authCheck$) {
      this.authCheck$ = this.http
        .get<CheckAuthResponse>(environment.authCheckUrl, { withCredentials: true })
        .pipe(
          map(res => {
            const r = res.results;

            // Persist UM's token so the interceptor can attach it to /api/ requests
            if (r.token) {
              AuthService.storeToken(r.token);
            } else {
              AuthService.clearToken();
            }

            // check-auth is the only source of identity — no local profile lookup.
            const user: User = {
              id: r.id,
              name: r.full_name,
              email: r.email,
              // Drives which products appear in the side-nav product switcher.
              allowedProducts: deriveAllowedProducts(r),
              // Human-facing department label — gates the side-nav Go Live CTA.
              displayDepartment: r.display_department_name,
            };
            return user;
          }),
          tap(user => this.userSource.next(user)),
          map(() => true),
          catchError(() => {
            this.redirectToLogin();
            return of(false);
          }),
          shareReplay(1),
        );
    }
    return this.authCheck$;
  }

  redirectToLogin(): void {
    window.location.href = environment.loginRedirectUrl;
  }

  logout(): void {
    // 1) Call UM logout, sending the token as ACM's interceptor does, so UM can
    //    invalidate it server-side. Capped at 5s so a hung UM can't block logout.
    const token = AuthService.getToken();
    this.http
      .get(environment.logoutUrl, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .pipe(
        timeout(5_000),
        catchError(() => of(null)),
        // 2) Finally — success, failure or timeout — clear the token, then
        // 3) redirect. Both live here, in order: `finalize` runs after the
        //    subscriber's callbacks, so a redirect in `subscribe` would fire first.
        finalize(() => {
          this.userSource.next(null);
          this.authCheck$ = null;
          AuthService.clearToken();
          this.redirectToLogin();
        }),
      )
      .subscribe();
  }
}
